import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import EditableGrid from "../../components/editableGrid";
import { withApplicationInsights } from "../../components/telemetry";
import {
  nameColumn,
  coachingTimeColumn,
  sessionsColumn,
  nextSessionColumn,
} from "../../models/userRepresentation";
import { GridActionsCellItem, GridColDef, GridRowId } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import { TooltipIcon } from "../../components/tooltipIcon";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import { Coachee, Session, SessionStatus } from "../../models";
import { CoacheeService } from "../../services/coachingNavigator/coacheeService";
import { SessionService } from "../../services/coachingNavigator/sessionService";
import config from "../../config";
import CreateClientPanel from "../../components/coachingNavigator/createClientPanel";
import dateFormatter from "../../util/dateFormatter";
import {
  LIBRARY_PAGE_HEADER_HEIGHT,
  LibraryPageHeader,
} from "../../components/libraryPageHeader";
import { Context } from "../../components/context";

const coacheeService = new CoacheeService(config.api.baseUrl, "/coachee");
const sessionService = new SessionService(config.api.baseUrl, "/sessions");

const CoachingHub: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const appContext = React.useContext(Context);
  const creatorEmail = React.useMemo(
    () => (appContext.state.invoker?.email || "").trim(),
    [appContext.state.invoker?.email],
  );
  const [allCoachees, setAllCoachees] = useState<Coachee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [clientPanelOpen, setClientPanelOpen] = useState(false);
  const [nextSessionsByCoachee, setNextSessionsByCoachee] = useState<
    Record<string, Session>
  >({});
  const [sessionCountersByCoachee, setSessionCountersByCoachee] = useState<
    Record<string, { completed: number; remaining: number }>
  >({});
  const [searchText, setSearchText] = useState("");

  const sortCoacheesByName = useCallback((items: Coachee[]) => {
    return [...items].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      }),
    );
  }, []);

  const formatHours = useCallback((value?: number) => {
    const safe = Number.isFinite(value) ? Math.max(Number(value), 0) : 0;
    const wholeHours = Math.floor(safe);
    const minutesTotal = Math.round((safe - wholeHours) * 60);
    const normalizedHours = wholeHours + Math.floor(minutesTotal / 60);
    const normalizedMinutes = minutesTotal % 60;

    if (normalizedMinutes === 0) return `${normalizedHours}h`;
    return `${normalizedHours}h ${normalizedMinutes}m`;
  }, []);

  const onClientDetails = useCallback(
    (row?: Coachee) => {
      if (!row?.id) return;
      navigate(`/coaching-hub/${row.id}`);
    },
    [navigate],
  );

  const onSessionOverview = useCallback(
    (row?: Coachee) => {
      if (row?.id) {
        navigate(`/sessionOverview/${row.id}`);
        return;
      }
      navigate(`/sessions`);
    },
    [navigate],
  );

  const onNextSession = useCallback(
    (row?: Coachee) => {
      const key = row?.id ? row.id.toString() : undefined;
      if (!key) return;
      const next = nextSessionsByCoachee[key];
      const sessionId =
        next?.id ??
        (next as unknown as { sessionId?: string; _id?: string })?.sessionId ??
        (next as unknown as { _id?: string })?._id;
      if (sessionId) {
        navigate(`/sessions/${sessionId}`, {
          state: { returnTo: "/coaching-hub" },
        });
      } else {
        navigate("/sessions");
      }
    },
    [navigate, nextSessionsByCoachee],
  );

  const gridActions = useCallback(
    (id: GridRowId, row: Coachee) => {
      return [
        <GridActionsCellItem
          key="clientDetails"
          icon={
            <TooltipIcon
              titleKey={"coachingNavigator.clientDetails"}
              icon={<PersonOutlineOutlinedIcon />}
            />
          }
          label={t("coachingNavigator.clientDetails")}
          onClick={() => onClientDetails(row)}
          style={{ color: "var(--mui-palette-primary-main)" }}
        />,
        <GridActionsCellItem
          key="sessionOverview"
          icon={
            <TooltipIcon
              titleKey={"coachingNavigator.sessionOverview"}
              icon={<EventNoteOutlinedIcon />}
            />
          }
          label={t("coachingNavigator.sessionOverview")}
          onClick={() => onSessionOverview(row)}
          style={{ color: "var(--mui-palette-primary-main)" }}
        />,
        <GridActionsCellItem
          key="nextSession"
          icon={
            <TooltipIcon
              titleKey={"coachingNavigator.nextSession"}
              icon={<KeyboardArrowRightOutlinedIcon />}
            />
          }
          label={t("coachingNavigator.nextSession")}
          onClick={() => onNextSession(row)}
          style={{ color: "var(--mui-palette-primary-main)" }}
        />,
      ];
    },
    [onClientDetails, onSessionOverview, onNextSession, t],
  );

  const renderCoachingTime = useCallback(
    (row: Coachee) => {
      const booked = row.bookedHours ?? 0;
      const completed = row.completedHours ?? 0;
      const remaining = Math.max(booked - completed, 0);
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1
          }}>
          <Typography variant="body2">{`${formatHours(booked)} ${t("coachingNavigator.scheduled")}`}</Typography>
          <Typography variant="body2">{`${formatHours(completed)} ${t("coachingNavigator.completed")}`}</Typography>
          <Typography variant="body2">{`${formatHours(remaining)} ${t("coachingNavigator.remaining")}`}</Typography>
        </Box>
      );
    },
    [formatHours, t],
  );

  const renderSessions = useCallback(
    (row: Coachee) => {
      const key = row.id ? row.id.toString() : "";
      const counters = key ? sessionCountersByCoachee[key] : undefined;
      const completed = counters?.completed ?? 0;
      const remaining = counters?.remaining ?? 0;
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1
          }}>
          <Typography variant="body2">{`${completed} ${t("coachingNavigator.completed")}`}</Typography>
          <Typography variant="body2">{`${remaining} ${t("coachingNavigator.remaining")}`}</Typography>
        </Box>
      );
    },
    [sessionCountersByCoachee, t],
  );

  const renderNextSession = useCallback(
    (row: Coachee) => {
      const key = row.id ? row.id.toString() : undefined;
      const session = key ? nextSessionsByCoachee[key] : undefined;
      if (!session) return "";
      const when = session.startTime;
      if (!when) return "";
      const date = new Date(when);
      return dateFormatter.format(date);
    },
    [nextSessionsByCoachee],
  );

  // Memoize columns to prevent recreating on every render
  const columns = useMemo(
    (): GridColDef[] => [
      {
        ...nameColumn,
        headerName: t("coachingNavigator.name"),
        flex: 0.3,
        minWidth: 100,
        maxWidth: 200,
      },
      {
        ...coachingTimeColumn,
        headerName: t("coachingNavigator.coachingTime"),
        flex: 0.35,
        minWidth: 360,
        renderCell: ({ row }) => renderCoachingTime(row as Coachee),
      },
      {
        ...sessionsColumn,
        headerName: t("coachingNavigator.sessions"),
        flex: 0.2,
        minWidth: 300,
        renderCell: ({ row }) => renderSessions(row as Coachee),
      },
      {
        ...nextSessionColumn,
        headerName: t("coachingNavigator.nextSession"),
        flex: 0.15,
        minWidth: 160,
        maxWidth: 160,
        renderCell: ({ row }) => renderNextSession(row as Coachee),
      },
    ],
    [t, renderCoachingTime, renderNextSession, renderSessions],
  );

  useEffect(() => {
    const load = async () => {
      try {
        if (!creatorEmail) {
          setAllCoachees([]);
          return;
        }

        const data = await coacheeService.getByCreator(creatorEmail);
        const activeCoachees = (data || []).filter((c) => c.active === true);
        const sorted = sortCoacheesByName(activeCoachees);
        setAllCoachees(sorted);
      } catch (err) {
        console.error("Failed to load coachees", err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [creatorEmail, sortCoacheesByName]);

  const gridItems = useMemo<Coachee[]>(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return allCoachees;
    const filtered = (allCoachees || []).filter(
      (c) =>
        c.name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.id?.toString().toLowerCase().includes(term),
    );
    return sortCoacheesByName(filtered);
  }, [allCoachees, searchText, sortCoacheesByName]);

  useEffect(() => {
    const loadSessionCounters = async () => {
      try {
        if (!creatorEmail) {
          setSessionCountersByCoachee({});
          return;
        }

        const sessions = await sessionService.getByCreator(creatorEmail);
        const counters: Record<string, { completed: number; remaining: number }> =
          {};

        (sessions || []).forEach((session) => {
          if (session.archived || !session.clientId) return;
          const key = session.clientId.toString();
          if (!counters[key]) {
            counters[key] = { completed: 0, remaining: 0 };
          }

          if (session.status === SessionStatus.Completed) {
            counters[key].completed += 1;
          } else {
            counters[key].remaining += 1;
          }
        });

        setSessionCountersByCoachee(counters);
      } catch (err) {
        console.error("Failed to load session counters", err);
      }
    };

    loadSessionCounters();
  }, [creatorEmail]);

  useEffect(() => {
    const loadNextSessions = async () => {
      try {
        const sessions = await sessionService.getNext();
        const map: Record<string, Session> = {};
        (sessions || []).forEach((s) => {
          if (!s.clientId) return;
          const sessionId =
            s.id ??
            (s as unknown as { sessionId?: string; _id?: string })?.sessionId ??
            (s as unknown as { _id?: string })?._id;
          map[s.clientId.toString()] = sessionId ? { ...s, id: sessionId } : s;
        });
        setNextSessionsByCoachee(map);
      } catch (err) {
        console.error("Failed to load next sessions", err);
      }
    };
    loadNextSessions();
  }, []);

  return (
    <Box
      sx={{ pt: 2, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <LibraryPageHeader
        title={t("coachingNavigator.coachingHub")}
        chipLabel={t("coachingNavigator.coachee", {
          count: gridItems.length,
        })}
        rightContent={
          <>
            <TextField
              size="small"
              placeholder={t("coachingNavigator.searchPlaceholderCoachee")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                width: 240,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                },
              }} slotProps={{ input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: "text.secondary", fontSize: "1.25rem" }}
                    />
                  </InputAdornment>
                ),
              } }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setClientPanelOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            >
              {t("coachingNavigator.addCoachee")}
            </Button>
          </>
        }
      />

      <Box sx={{ flexGrow: 1 }}>
        <EditableGrid
          columns={columns}
          extraActions={gridActions}
          rows={gridItems}
          actionsHeaderKey=""
          height={`calc(90vh - ${LIBRARY_PAGE_HEADER_HEIGHT}px)`}
          toolbar={() => null}
          loading={isLoading}
        />
      </Box>
      <CreateClientPanel
        open={clientPanelOpen}
        onClose={() => setClientPanelOpen(false)}
        onCreated={(coachee) => {
          if (coachee.active === true) {
            setAllCoachees((prev) => sortCoacheesByName([...prev, coachee]));
          }
        }}
      />
    </Box>
  );
};

export default withApplicationInsights(CoachingHub, "CoachingHub");
